import { useLayoutEffect, useState } from 'react';

// Distance from the top of the document. offsetTop ignores transforms, so an
// entrance animation still running doesn't skew the result.
const getDocumentTop = (element) => {
  let top = 0;
  for (let node = element; node; node = node.offsetParent) top += node.offsetTop;
  return top;
};

/**
 * Height that lets `ref`'s element end `bottomGap` px above the bottom of the
 * viewport, so the page needs no scrolling to show its end. Recomputed when
 * the window or the page above it changes size; never below `minHeight` (short
 * screens scroll instead of squeezing the content).
 */
const useViewportFit = (ref, { bottomGap = 24, minHeight = 0 } = {}) => {
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => {
      const next = Math.max(minHeight, Math.floor(window.innerHeight - getDocumentTop(element) - bottomGap));
      setHeight((prev) => (prev === next ? prev : next));
    };
    update();
    window.addEventListener('resize', update);
    // Banners, wrapping headers or late fonts above the element move it down
    const observer = new ResizeObserver(update);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [ref, bottomGap, minHeight]);

  return height;
};

export default useViewportFit;
